import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { moduleName } from './constants'
import { IChatHistoryState, IChatHistory } from './types'
import { ResponseWithPagination } from '../../../constants'

const initialState: IChatHistoryState = {
  chatHistory: [],
  currentChatId: undefined,
  total: 0,
  isLoading: false,
  isLoaded: false,
  error: null,
  messageToReply: null,
  searchMessages: []
}

const slice = createSlice({
  name: moduleName,
  initialState,
  reducers: {
    loading(state: IChatHistoryState) {
      state.isLoading = true
      state.error = null
    },
    finish(state: IChatHistoryState) {
      state.isLoading = false
    },
    error(
      state: IChatHistoryState,
      action: PayloadAction<{ error: IChatHistoryState['error'] }>
    ) {
      const { error } = action.payload
      state.error = error
    },
    reset(state: IChatHistoryState) {
      Object.keys(initialState).forEach((key) => {
        state[key as string] = initialState[key]
      })
    },
    setChatHistory(
      state: IChatHistoryState,
      action: PayloadAction<{
        results: IChatHistory[]
        count: number
        chatId: string
      }>
    ) {
      state.chatHistory = action.payload.results
      state.total = action.payload.count
      state.currentChatId = action.payload.chatId
    },
    addNewMessage(
      state: IChatHistoryState,
      action: PayloadAction<{
        message: IChatHistory
        chatId: string
      }>
    ) {
      const { message, chatId } = action.payload
      if (chatId === state.currentChatId) {
        state.chatHistory = [message, ...state.chatHistory]
        state.total = state.total + 1
      }
    },
    updateReadByUsers(
      state: IChatHistoryState,
      action: PayloadAction<{ chatId: string }>
    ) {
      const { chatId } = action.payload
      if (chatId === state.currentChatId) {
        state.chatHistory = state.chatHistory?.map((i) => ({
          ...i,
          read_by_members: true
        }))
      }
    },
    appendToChatHistory(
      state: IChatHistoryState,
      action: PayloadAction<ResponseWithPagination<IChatHistory>>
    ) {
      state.chatHistory = [...state.chatHistory, ...action.payload.results]
      state.total = action.payload.count
    },
    setMessageToReply(
      state: IChatHistoryState,
      action: PayloadAction<Omit<IChatHistory, 'read'>>
    ) {
      state.messageToReply = { ...action.payload, read: false }
    },
    resetMessageToReply(state: IChatHistoryState) {
      state.messageToReply = null
    },
    setSearchHistory(
      state: IChatHistoryState,
      action: PayloadAction<{ results: IChatHistory[] }>
    ) {
      state.searchMessages = action.payload.results
    },
    resetSearchHistory(state: IChatHistoryState) {
      state.searchMessages = []
    }
  }
})

export default slice.reducer
export const {
  loading,
  finish,
  error,
  reset,
  setChatHistory,
  addNewMessage,
  appendToChatHistory,
  setMessageToReply,
  resetMessageToReply,
  setSearchHistory,
  resetSearchHistory,
  updateReadByUsers
} = slice.actions
